import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ProgressBar } from '../components/ProgressBar';
import { Badge } from '../components/Badge';

interface Resp {
  candidate: { id: string; name: string; email: string };
  attempts: {
    id: string;
    phase: 'benchmark' | 'dojo';
    status: string;
    scorePct?: number;
    passed?: boolean;
    submittedAt?: string;
    perCategory?: Record<string, { correct: number; total: number }>;
  }[];
  progress: {
    benchmarkPassed: boolean;
    dojoMastered: boolean;
    bestBenchmark?: number;
    bestDojo?: number;
    weakest?: string[];
    badges: string[];
  };
}

export function AdminCandidate() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Resp | null>(null);

  useEffect(() => {
    if (!id) return;
    api.get<Resp>(`/api/admin/candidates/${id}`).then(setData);
  }, [id]);

  if (!data) return <p className="loading">Loading…</p>;
  return (
    <div className="page-admin">
      <p>
        <Link to="/admin">← Back to admin</Link>
      </p>
      <h1>{data.candidate.name}</h1>
      <p className="muted">{data.candidate.email}</p>

      <section className="dashboard-grid">
        <div className="card">
          <h3>Benchmark</h3>
          <ProgressBar value={data.progress.bestBenchmark ?? 0} label="Best score" />
        </div>
        <div className="card">
          <h3>Dojo</h3>
          <ProgressBar value={data.progress.bestDojo ?? 0} label="Best score" variant="mastery" />
        </div>
        <div className="card">
          <h3>Badges</h3>
          <div className="badge-row">
            {data.progress.badges.map((b) => (
              <Badge key={b} kind={b} />
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2>Attempts</h2>
        {data.attempts.map((a) => (
          <div className="card" key={a.id}>
            <h3>
              {a.phase} — {a.scorePct ?? '—'}% {a.passed ? '✔' : ''}
            </h3>
            <p className="muted">
              {a.status} · {a.submittedAt ? new Date(a.submittedAt).toLocaleString() : 'in progress'}
            </p>
            {a.perCategory && (
              <div>
                {Object.entries(a.perCategory)
                  .filter(([, v]) => v.total > 0)
                  .map(([cat, v]) => (
                    <ProgressBar
                      key={cat}
                      value={v.total === 0 ? 0 : Math.round((v.correct / v.total) * 100)}
                      label={`${cat} — ${v.correct}/${v.total}`}
                    />
                  ))}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
